/**
 * UnassignedBucket — pinned-at-top bucket for documents the assignment step
 * couldn't route. Each row offers a manual "zuweisen" picker.
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconAlertTriangle, IconChevronRight } from "@tabler/icons-react"
import clsx from "clsx"
import type { DragEvent } from "react"
import { useState } from "react"
import type { PendingDocumentOut, QmsRecord } from "../../../types"
import { DOC_DRAG_MIME, PendingDocumentRow } from "./PendingDocumentRow"

interface Props {
  docs: PendingDocumentOut[]
  suppliers: QmsRecord[]
  pkPrimitiveName: string | null
  namePrimitiveName: string | null
  downloadUrl: (docId: string) => string
  onAssign: (docId: string, recordId: string) => void
  onAcceptFiles: (files: File[]) => void
  onAcceptDocReassign: (docId: string) => void
  onDeleteDoc: (docId: string) => void
  newDocumentIds?: Set<string>
}

export function UnassignedBucket({
  docs,
  suppliers,
  pkPrimitiveName,
  namePrimitiveName,
  downloadUrl,
  onAssign,
  onAcceptFiles,
  onAcceptDocReassign,
  onDeleteDoc,
  newDocumentIds,
}: Props) {
  const { translate } = useTranslation()
  const [expanded, setExpanded] = useState(true)
  const [isDragOver, setIsDragOver] = useState(false)

  const handleDragOver = (e: DragEvent) => {
    if (
      e.dataTransfer.types.includes(DOC_DRAG_MIME) ||
      e.dataTransfer.types.includes("Files")
    ) {
      e.preventDefault()
      e.dataTransfer.dropEffect = "move"
      setIsDragOver(true)
    }
  }

  const handleDragLeave = () => setIsDragOver(false)

  const handleDrop = (e: DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const docId = e.dataTransfer.getData(DOC_DRAG_MIME)
    if (docId) {
      onAcceptDocReassign(docId)
      return
    }
    const files = Array.from(e.dataTransfer.files ?? [])
    if (files.length > 0) onAcceptFiles(files)
  }

  if (docs.length === 0) return null

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={clsx(
        "rounded-lg border bg-grey1 transition-colors",
        isDragOver ? "border-yellow1 bg-yellow1/5" : "border-yellow1/40",
      )}
    >
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left"
      >
        <IconChevronRight
          size={14}
          className={clsx(
            "shrink-0 text-yellow1 transition-transform",
            expanded && "rotate-90",
          )}
        />
        <IconAlertTriangle size={14} className="shrink-0 text-yellow1" />
        <span className="text-sm font-medium text-yellow1">
          {translate("QMS/SUPPLIER/UNASSIGNED")}
        </span>
        <span className="text-xs text-grey5">
          ·{" "}
          {translate("QMS/SUPPLIER/DOCUMENT_COUNT", {
            count: docs.length,
            documentLabel:
              docs.length === 1
                ? translate("QMS/SUPPLIER/DOCUMENT_SINGULAR")
                : translate("QMS/SUPPLIER/DOCUMENT_PLURAL"),
          })}
        </span>
      </button>
      {expanded && (
        <div>
          {docs.map((doc) => (
            <UnassignedRow
              key={doc.id}
              doc={doc}
              suppliers={suppliers}
              pkPrimitiveName={pkPrimitiveName}
              namePrimitiveName={namePrimitiveName}
              downloadUrl={downloadUrl(doc.id)}
              onAssign={(recordId) => onAssign(doc.id, recordId)}
              onDelete={() => onDeleteDoc(doc.id)}
              isNew={newDocumentIds?.has(doc.id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function UnassignedRow({
  doc,
  suppliers,
  pkPrimitiveName,
  namePrimitiveName,
  downloadUrl,
  onAssign,
  onDelete,
  isNew,
}: {
  doc: PendingDocumentOut
  suppliers: QmsRecord[]
  pkPrimitiveName: string | null
  namePrimitiveName: string | null
  downloadUrl: string
  onAssign: (recordId: string) => void
  onDelete: () => void
  isNew?: boolean
}) {
  const { translate } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <div className="border-t border-grey4/30">
      <PendingDocumentRow
        doc={doc}
        downloadUrl={downloadUrl}
        onDelete={onDelete}
        isNew={isNew}
      />
      <div className="flex items-center gap-2 px-4 pb-2">
        <span className="text-[11px] text-grey5">→</span>
        {open ? (
          <select
            autoFocus
            onBlur={() => setOpen(false)}
            onChange={(e) => {
              const v = e.target.value
              if (v) {
                onAssign(v)
                setOpen(false)
              }
            }}
            className="flex-1 rounded border border-grey4 bg-grey3 px-2 py-1 text-xs text-white focus:border-yellow1 focus:outline-hidden"
            defaultValue=""
          >
            <option value="" disabled>
              {translate("QMS/SUPPLIER/CHOOSE_SUPPLIER")}
            </option>
            {suppliers.map((s) => {
              const pk = pkPrimitiveName
                ? (s.values[pkPrimitiveName]?.value ?? s.id)
                : s.id
              const name = namePrimitiveName
                ? (s.values[namePrimitiveName]?.value ?? pk)
                : pk
              return (
                <option key={s.id} value={s.id}>
                  {name} {pk !== name ? `(${pk})` : ""}
                </option>
              )
            })}
          </select>
        ) : (
          <button
            onClick={() => setOpen(true)}
            className="rounded border border-grey4 px-2 py-0.5 text-[11px] text-grey5 hover:border-yellow1 hover:text-yellow1"
          >
            {translate("QMS/SUPPLIER/ASSIGN")}
          </button>
        )}
      </div>
    </div>
  )
}
